import { EMPLOYEE_ROLE_LABELS } from "@etudescarre/constants";
import {
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  Modal,
} from "@etudescarre/ui";
import {
  ArrowClockwiseIcon,
  TrashIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";

type PendingInvite = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: "root" | "admin" | "user";
  createdAt: string;
};

export type PendingInvitesListProps = {
  onInviteRevoked?: () => void;
};

export function PendingInvitesList({
  onInviteRevoked,
}: PendingInvitesListProps) {
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [inviteToRevoke, setInviteToRevoke] = useState<PendingInvite | null>(
    null,
  );
  const [isRevoking, setIsRevoking] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    const { data, error: fetchError } = await apiClient.GET(
      "/api/admin/users/invites",
    );
    if (fetchError) {
      setError("Erreur lors du chargement des invitations");
      setLoading(false);
      return;
    }

    setInvites(
      data.data.invites.map((invite) => ({
        id: invite.id,
        firstName: invite.firstName,
        lastName: invite.lastName,
        // An invite always carries an email and a role: sending one
        // requires both (see admin/users/invite.ts).
        email: invite.email ?? "",
        role: invite.role ?? "user",
        createdAt: invite.createdAt,
      })),
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleResend = async (invite: PendingInvite) => {
    setResendingId(invite.id);
    const { error: resendError } = await apiClient.POST(
      "/api/admin/users/{userId}/resend-invite",
      {
        params: { path: { userId: invite.id } },
      },
    );

    if (resendError) {
      toaster.error({
        title: "Erreur",
        description: "Échec du renvoi de l'invitation",
      });
    } else {
      toaster.success({
        title: "Succès",
        description: `Invitation renvoyée à ${invite.email}`,
      });
    }
    setResendingId(null);
  };

  const handleRevokeClick = (invite: PendingInvite) => {
    setInviteToRevoke(invite);
    setRevokeModalOpen(true);
  };

  const handleRevokeConfirm = async () => {
    if (!inviteToRevoke) return;

    setIsRevoking(true);
    const { error: revokeError } = await apiClient.DELETE(
      "/api/admin/users/{userId}/invite",
      {
        params: { path: { userId: inviteToRevoke.id } },
      },
    );

    if (revokeError) {
      toaster.error({
        title: "Erreur",
        description: "Échec de la révocation de l'invitation",
      });
    } else {
      toaster.success({
        title: "Succès",
        description: `Invitation de ${inviteToRevoke.email} révoquée`,
      });
      await fetchInvites();
      onInviteRevoked?.();
    }

    setIsRevoking(false);
    setRevokeModalOpen(false);
    setInviteToRevoke(null);
  };

  const columns: DataTableColumn<PendingInvite>[] = [
    {
      id: "name",
      header: "Nom",
      accessor: (row) =>
        [row.firstName, row.lastName].filter(Boolean).join(" ") || "-",
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "email",
      header: "Email",
      accessor: "email",
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "role",
      header: "Rôle",
      accessor: (row) => EMPLOYEE_ROLE_LABELS[row.role],
      cell: ({ row }) => {
        const roleColors: Record<
          PendingInvite["role"],
          "purple" | "blue" | "default"
        > = {
          root: "purple",
          admin: "blue",
          user: "default",
        };
        return (
          <Badge variant={roleColors[row.role]}>
            {EMPLOYEE_ROLE_LABELS[row.role]}
          </Badge>
        );
      },
      loadingCell: "badge",
    },
    {
      id: "status",
      header: "Statut",
      accessor: () => "En attente",
      cell: () => <Badge variant="yellow">En attente</Badge>,
      loadingCell: "badge",
    },
    {
      id: "createdAt",
      header: "Invité le",
      accessor: (row) => new Date(row.createdAt).toLocaleDateString("fr-FR"),
      loadingCell: "text",
    },
    {
      id: "actions",
      header: "Actions",
      accessor: () => "",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            plain
            onClick={() => handleResend(row)}
            disabled={resendingId === row.id}
            aria-label="Renvoyer l'invitation"
            title="Renvoyer l'invitation"
          >
            <ArrowClockwiseIcon className="size-4" />
          </Button>
          <Button
            plain
            onClick={() => handleRevokeClick(row)}
            aria-label="Révoquer l'invitation"
            title="Révoquer l'invitation"
          >
            <TrashIcon className="size-4 text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={invites}
        isLoading={loading}
        isEmpty={invites.length === 0}
        emptyState={
          <div className="flex min-h-[200px] items-center justify-center text-gray-500">
            Aucune invitation en attente
          </div>
        }
      />

      <Modal.Root
        open={revokeModalOpen}
        onOpenChange={(details) => {
          if (!isRevoking) {
            setRevokeModalOpen(details.open);
          }
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content size="sm">
              <Modal.Header>
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <WarningIcon className="size-5 text-red-600 dark:text-red-400" />
                  </div>
                  <Modal.Title>Révoquer l'invitation</Modal.Title>
                </div>
              </Modal.Header>
              <Modal.Body>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  Êtes-vous sûr de vouloir révoquer l'invitation de{" "}
                  <strong>{inviteToRevoke?.email}</strong> ? Cette action est
                  irréversible et supprimera le compte.
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setRevokeModalOpen(false)}
                  disabled={isRevoking}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleRevokeConfirm}
                  disabled={isRevoking}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  {isRevoking ? "En cours..." : "Révoquer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}
